<script lang="ts">
	import { onMount } from 'svelte'
	import AnalysisScaleBadge from '$lib/analytics/AnalysisScaleBadge.svelte'
	import type { Result } from '$lib/analytics/types/analyticalEvaluation'

	interface Props {
		result: Result
	}

	let { result }: Props = $props()

	let scaleElement = $state<HTMLElement | null>(null)
	let valueBadge = $state<HTMLElement | null>(null)
	let scaleWidth = $state(0)

	const valuePositionLeft = $derived.by(() => {
		if (!scaleWidth) return 0
		if (result.min === undefined || result.max === undefined) {
			return scaleWidth / 2
		}
		if (result.max <= result.min) {
			return scaleWidth / 2
		}

		const percent = (result.value - result.min) / (result.max - result.min)
		const clamped = Math.min(1, Math.max(0, percent))
		return Math.max(0, Math.min(scaleWidth - 2, scaleWidth * clamped - 2)) // 2px for line width
	})

	const badgePositionLeft = $derived.by(() => {
		if (!valueBadge || !scaleWidth) return 0

		const badgeWidth = valueBadge.clientWidth
		const centered = valuePositionLeft - badgeWidth / 2

		return Math.max(0, Math.min(centered, scaleWidth - badgeWidth))
	})

	onMount(() => {
		if (!scaleElement) return

		const updateWidth = () => {
			scaleWidth = scaleElement?.clientWidth ?? 0
		}

		updateWidth()

		// In case this component is rendered inside a tooltip e.g., we need to use a ResizeObserver to get the actual width
		// of the scale element.
		const observer = new ResizeObserver(() => {
			updateWidth()
		})

		observer.observe(scaleElement)

		return () => observer.disconnect()
	})
</script>

<div class="analysis-scale-container">
	<div bind:this={scaleElement} class="analysis-scale">
		<div
			class="analysis-scale__value analysis-scale__value-line"
			style={`left: ${valuePositionLeft}px`}
		></div>
		<div
			class="analysis-scale__value analysis-scale__value-badge"
			bind:this={valueBadge}
			style={`left: ${badgePositionLeft}px`}
		>
			<AnalysisScaleBadge
				valueString={result.valueString}
				value={result.value}
				ordinal={result.priority}
				maxOrdinal={result.maxOrdinal}
				unit={result.unit}
			/>
		</div>
		<div class="analysis-scale__adjacent-classes">
			<span class="analysis-scale__adjacent-class-text">
				{#if result.previousClass && result.min !== undefined && result.previousOrdinal !== undefined}
					{[result.previousSign ?? '', result.min, result.unit ?? '', result.previousClass].join(
						' '
					)}
				{/if}
			</span>
			<span class="analysis-scale__adjacent-class-text">
				{#if result.nextClass && result.max !== undefined && result.nextOrdinal !== undefined}
					{[result.nextSign ?? '', result.max, result.unit ?? '', result.nextClass].join(' ')}
				{/if}
			</span>
		</div>
	</div>
</div>

<style>
	.analysis-scale-container {
		display: flex;
		align-items: center;
		height: 62px;
		width: 100%;
		max-width: 400px;
	}

	.analysis-scale {
		position: relative;
		width: 100%;
		min-width: 251px;
		height: 4px;
		border-radius: 4px;
		background-color: var(--color-gray-200);
	}

	.analysis-scale__value {
		position: absolute;
	}

	.analysis-scale__value-line {
		width: 2px;
		height: 10px;
		top: -3px;
		border-radius: 1px;
		background-color: var(--color-gray-600);
	}

	.analysis-scale__value-badge {
		bottom: 10px;
	}

	.analysis-scale__adjacent-classes {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		padding-top: 8px;
	}

	.analysis-scale__adjacent-class-text {
		color: var(--color-gray-800);
		font-weight: 400;
		line-height: 1.25rem;
	}
</style>
